import React, {useState} from 'react';
import {Form} from 'react-bootstrap';
import graphql from "babel-plugin-relay/macro";
import {useFragment, useMutation} from "react-relay";
import {FormProvider, useForm} from "react-hook-form";
import {useUser} from "../../hooks/useUser";
import {toast} from "react-toastify";
import InfoPanel from "../../components/common/repeated-page-panels/InfoPanel";
import {createOptions, yesNoUnknownOptions} from "../../helpers/utils";
import {allContractsDropdown} from "../contracts/ContractFragments";
import HorizontalFormSelect from "../../components/common/horizontal-form-inputs/HorizontalFormSelect";
import HorizontalFormInput from "../../components/common/horizontal-form-inputs/HorizontalFormInput";
import HorizontalFormCheck from "../../components/common/horizontal-form-inputs/HorizontalFormCheck";
import HorizontalFormDate from "../../components/common/horizontal-form-inputs/HorizontalFormDate";
import dayjs from "dayjs";

const PractitionerInfo = ({practitioner, legalContracts, displayMode}) => {
    const mutation = graphql`
    mutation PractitionerInfoMutation($id: ID!, $legalContractId: ID!) {
        practitioner {
            modify(input: {practitionerId: $id, legalContractId: $legalContractId}) {
                practitioner {
                    ...PractitionerInfo_Practitioner
                }
            }
        }
    }`
    const practitionerInfo = useFragment(graphql`
    fragment PractitionerInfo_Practitioner on Practitioner {
        id
        legalContract {
            id
            name
            linesOfService {
                ...ContractLinesOfServiceFragments_LinesOfService
            }
            locations {
                ...LocationFragments_LocationDropdown
            }
        }
        applicableVersion {
           displayName
           firstName
           lastName
           middleInitial
           nickName
           nameSuffix
           qualification
           dateOfBirth
           allowedToBePcp
           credentialingDelegated
           willingToSeePeopleWithLimitedEnglish
           willingToSeeMinorities
           willingToSeePeopleWithDisabilities
           willingToSeePeopleWithDiverseSexualOrientations
           willingToSeePeopleWithDiverseGenderIdentities
           willingToSeePeopleLivingInRuralAreas
           willingToSeePeopleAdverselyAffectedByPovertyOrInequality
           gender {
               id
               name
           }
           ethnicity {
               id
               name
           }
           professionalDesignation {
               id
               name
           }
        }
        billingAddresses {
            ...ProviderBillingAddresses_Addresses
        }
        locations {
            ...PractitionerLocations_Locations
        }
    }`, practitioner)
    const allLegalContracts = createOptions(useFragment(allContractsDropdown, legalContracts), 'id', 'name');
    const [readMode, setReadMode] = useState(true);
    const [commitMutation, isInFlight] = useMutation(mutation);
    
    const defaultValues = {
        legalContractId: practitionerInfo.legalContract.id,
        firstName: practitionerInfo.applicableVersion.firstName,
        lastName: practitionerInfo.applicableVersion.lastName,
        middleInitial: practitionerInfo.applicableVersion.middleInitial,
        nickName: practitionerInfo.applicableVersion.nickName,
        nameSuffix: practitionerInfo.applicableVersion.nameSuffix,
        qualification: practitionerInfo.applicableVersion.qualification,
        dateOfBirth: practitionerInfo.applicableVersion.dateOfBirth ? dayjs(practitionerInfo.applicableVersion.dateOfBirth).format('M/D/YYYY') : null,
        gender: practitionerInfo.applicableVersion.gender?.name,
        ethnicity: practitionerInfo.applicableVersion.ethnicity?.name,
        professionalDesignation: practitionerInfo.applicableVersion.professionalDesignation?.name,
        allowedToBePcp: practitionerInfo.applicableVersion.allowedToBePcp,
        credentialingDelegated: practitionerInfo.applicableVersion.credentialingDelegated,
        willingToSeePeopleWithLimitedEnglish: practitionerInfo.applicableVersion.willingToSeePeopleWithLimitedEnglish,
        willingToSeeMinorities: practitionerInfo.applicableVersion.willingToSeeMinorities,
        willingToSeePeopleWithDisabilities: practitionerInfo.applicableVersion.willingToSeePeopleWithDisabilities,
        willingToSeePeopleWithDiverseSexualOrientations: practitionerInfo.applicableVersion.willingToSeePeopleWithDiverseSexualOrientations,
        willingToSeePeopleWithDiverseGenderIdentities: practitionerInfo.applicableVersion.willingToSeePeopleWithDiverseGenderIdentities,
        willingToSeePeopleLivingInRuralAreas: practitionerInfo.applicableVersion.willingToSeePeopleLivingInRuralAreas,
        willingToSeePeopleAdverselyAffectedByPovertyOrInequality: practitionerInfo.applicableVersion.willingToSeePeopleAdverselyAffectedByPovertyOrInequality,
    }

    const methods = useForm({values: defaultValues})
    const {hasPermission} = useUser();
    
    const onSubmit = (data) => {
        commitMutation({variables: {
            id: practitionerInfo.id,
            legalContractId: data.legalContractId
            }, onCompleted: () => {
                toast.success("Practitioner has been updated");
                setReadMode(true);
            }})
    }
    const handleCancelClick = () => {
        methods.reset();
        setReadMode(true);
    }
    return (
        <FormProvider {...methods}>
            <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                <InfoPanel readMode={readMode}
                           onCancelChangesClick={handleCancelClick}
                           name={practitionerInfo.applicableVersion.displayName}
                           isInFlight={isInFlight}
                           onUpdateDetailsClick={() => {setReadMode(false)}}
                           showUpdate={displayMode === "current" ? false : hasPermission("Practitioner.Update")}
                           >
                    <HorizontalFormSelect options={allLegalContracts} 
                                          controlId={"legalContractId"} 
                                          label={"Contract"} 
                                          validation={{required: "Contract is required"}} 
                                          readMode={readMode} />
                    {displayMode === "current" && <>
                        <HorizontalFormInput controlId={"firstName"} label={"First Name"} readMode={true} />
                        <HorizontalFormInput controlId={"middleInitial"} label={"Middle Initial"} readMode={true} />
                        <HorizontalFormInput controlId={"lastName"} label={"Last Name"} readMode={true} />
                        <HorizontalFormInput controlId={"nameSuffix"} label={"Name Suffix"} readMode={true} />
                        <HorizontalFormInput controlId={"nickName"} label={"Nick Name"} readMode={true} />
                        <HorizontalFormInput controlId={"qualification"} label={"Qualification"} readMode={true} />
                        <HorizontalFormInput controlId={"professionalDesignation"} label={"Professional Designation"} readMode={true} />
                        <HorizontalFormInput controlId={"gender"} label={"Gender"} readMode={true} />
                        <HorizontalFormInput controlId={"ethnicity"} label={"Ethnicity"} readMode={true} />
                        <HorizontalFormInput controlId={"dateOfBirth"} label={"Date of Birth"} readMode={true} />
                        <HorizontalFormCheck controlId={"allowedToBePcp"} label={"Allowed to Be Pcp"} readMode={true} />
                        <HorizontalFormCheck controlId={"credentialingDelegated"} label={"Credentialing Delegated"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeePeopleWithLimitedEnglish"} label={"Willing to see people with limited English"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeeMinorities"} label={"Willing to see minorities"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeePeopleWithDisabilities"} label={"Willing to see people with disabilities"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeePeopleWithDiverseSexualOrientations"} label={"Willing to see people with diverse sexual orientations"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeePeopleWithDiverseGenderIdentities"} label={"Willing to see people with diverse gender identities"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeePeopleLivingInRuralAreas"} label={"Willing to see people living in rural areas"} readMode={true} />
                        <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"willingToSeePeopleAdverselyAffectedByPovertyOrInequality"} label={"Willing to see people adversely affected by poverty or inequality"} readMode={true} />
                    </>}
                </InfoPanel>
            </Form>
        </FormProvider>
    );
};

export default PractitionerInfo;
